import type { Shipment } from '@/domains/shipment/types/Shipment';
import { db } from '@/db/dexieClient';
import { uow, UnitOfWork } from '@/db/unitOfWork';
import { liveQuery } from 'dexie';
import { upsertEntities, type SyncableEntity, type UpsertConfig } from '@/services/sync/syncUtils';
import { syncService, SyncJobTypes } from '@/services/sync/syncService';
import { addressService } from '@/domains/address/services/addressService';
import { useDateFormat } from '@/composables/useDateFormat';
import { useStoredUserGuid } from '@/composables/useStoredUserGuid';

const shipmentsByRouteIdQuery = (routeId: number) =>
  db.shipments.where('routeId').equals(routeId).toArray();

// Generic partial type for updates
// For Dexie .update, use shallow updates (no nested objects/arrays, just the ones that potentially might change)
export type ShipmentUpdate = Partial<
  Pick<
    Shipment,
    | 'scannedAddressId'
    | 'scannedByUserAccountId'
    | 'scannedOn'
    | 'scannedOnLocal'
    | 'statusCodeId'
    | 'syncStatus'
    | 'trackingNumberInputTypeId' // TODO: this should be set by the backend
    | 'updatedDate'
    | 'updatedBy'
    | 'scannedOnLatitude'
    | 'scannedOnLongitude'
    | 'firstDelivery'
    | 'scannedTimeZoneId'
  >
>;

// --- Public API: Get Operations ---
export const getShipmentsFromCacheByRouteId = async (routeId: number): Promise<Shipment[]> => {
  try {
    return await shipmentsByRouteIdQuery(routeId);
  } catch (error) {
    console.error('Failed to get shipments from cache by routeId:', error);
    return [];
  }
};

export const observeShipmentsByRouteId = (routeId: number) =>
  liveQuery(async () => (await shipmentsByRouteIdQuery(routeId)) ?? []);

export const getShipmentsByDestinationAddressId = async (
  addressId: number
): Promise<Shipment[]> => {
  try {
    return await db.shipments.where('destinationAddressId').equals(addressId).toArray();
  } catch (error) {
    console.error('Failed to get shipments for destination addressId: ', addressId, error);
    return [];
  }
};

export const getAllShipmentsFromCache = async (): Promise<Shipment[]> => {
  try {
    return await db.shipments.toArray();
  } catch (error) {
    console.error('Failed to get all shipments from cache:', error);
    return [];
  }
};

export const observeAllShipments = () =>
  liveQuery(async () => (await db.shipments.toArray()) ?? []);

// --- Public API: Update Operations ---
type ExistingShipmentRecord = { id: number; oldRecord: Shipment };

const normalizeShipmentIds = (shipmentIds: number | number[]): number[] =>
  Array.isArray(shipmentIds) ? shipmentIds : [shipmentIds];

const isExistingShipmentRecord = (record: {
  id: number;
  oldRecord: Shipment | undefined;
}): record is ExistingShipmentRecord => Boolean(record.oldRecord);

export const updateShipment = async (
  shipmentIds: number | number[],
  changes: ShipmentUpdate
): Promise<void> => {
  const { actions: dateActions } = useDateFormat();
  const { getUserGuid } = useStoredUserGuid();

  const ids = normalizeShipmentIds(shipmentIds);
  if (ids.length === 0) return;

  const updatedBy = (await getUserGuid()) ?? null;
  const appliedChanges = {
    ...changes,
    updatedDate: dateActions.utcNowDate(),
    updatedBy,
  };

  const records = await Promise.all(
    ids.map(async (id) => ({ id, oldRecord: await db.shipments.get(id) }))
  );
  const existingRecords = records.filter(isExistingShipmentRecord);
  if (existingRecords.length === 0) return;

  const bulkUpdatePayload = existingRecords.map((r) => ({
    key: r.id,
    changes: appliedChanges,
  }));

  await uow.runRW([db.shipments], async () => db.shipments.bulkUpdate(bulkUpdatePayload));

  try {
    for (const { oldRecord } of existingRecords) {
      const newRecord: Shipment = {
        ...oldRecord,
        ...appliedChanges,
      };
      await syncService.enqueue({
        type: SyncJobTypes.SHIPMENT_UPDATE,
        newRecord,
        oldRecord,
      });
    }
  } catch (error) {
    console.warn('[shipmentRepository] Failed to enqueue shipment update sync job:', {
      shipmentIds: ids,
      error: error instanceof Error ? error.message : String(error),
      changes,
    });
    throw error;
  }
};

// --- Public API: Sync Operations ---
export const upsertShipments = async (apiShipments: Shipment[]): Promise<Shipment[]> => {
  try {
    const cachedShipments = await getAllShipmentsFromCache();
    // Define the configuration for the shipment upsert operation
    const config: UpsertConfig<Shipment & SyncableEntity> = {
      getId: (shipment) => shipment.id,
      markAsPending: (shipment) => ({
        ...shipment,
        syncStatus: 'pending',
        syncedOn: null,
      }),
    };

    const { toInsert, toUpdate, toDelete, toKeepLocal } = upsertEntities(
      apiShipments as (Shipment & SyncableEntity)[],
      cachedShipments as (Shipment & SyncableEntity)[],
      config
    );

    await uow.runRW([db.shipments], async () => {
      // Insert new shipments
      if (toInsert.length > 0) {
        const plainShipments = UnitOfWork.toPlain(toInsert);
        await db.shipments.bulkPut(plainShipments);
      }

      // Update existing shipments with API data
      if (toUpdate.length > 0) {
        const plainShipments = UnitOfWork.toPlain(toUpdate);
        await db.shipments.bulkPut(plainShipments);
      }

      // Update shipments that should be kept locally (mark as pending)
      if (toKeepLocal.length > 0) {
        const plainShipments = UnitOfWork.toPlain(toKeepLocal);
        await db.shipments.bulkPut(plainShipments);
      }

      // Delete shipments not in API response
      if (toDelete.length > 0) {
        await db.shipments.bulkDelete(toDelete);
      }
    });

    // Get updated shipments from cache after upsert
    const updatedShipments = await getAllShipmentsFromCache();

    console.info('[shipmentRepository] Sync completed:', {
      inserted: toInsert.length,
      updated: toUpdate.length,
      deleted: toDelete.length,
      keptLocal: toKeepLocal.length,
    });

    if (toKeepLocal.length > 0)
      console.warn('[shipmentRepository] Shipments kept local:', toKeepLocal);

    // Load missing addresses from shipments
    await addressService.loadMissingAddressesFromEntities(undefined, updatedShipments);

    return updatedShipments;
  } catch (error) {
    console.error('Failed to upsert shipments:', error);
    throw error;
  }
};
